import { Button, Callout, Field, Input } from '@provenance/design';
import { loginSchema, type LoginInput } from '@provenance/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, ShieldCheck } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiError, api } from '../lib/api';
import { takeSignedOutReason } from '../lib/landing';
import { useSession } from '../lib/session';

/**
 * Sign in.
 *
 * The one screen where Provenir leads rather than a chamber — at this point we do not
 * know which chamber the person belongs to, so there is no brand to apply. The chamber's
 * identity takes over the moment the session resolves.
 */
export function SignIn() {
  const { signIn } = useSession();
  const [formError, setFormError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  // Why the last session ended, said once (T-140). A sign-out nobody asked for, with no
  // reason given, reads as a fault.
  const [endedBecause] = useState(takeSignedOutReason);

  /**
   * The response never varies, so neither does what we show. Any difference here — a
   * "no such account" message, an error state — would undo on the screen what the endpoint
   * is careful about on the wire: this must not become a way to discover which addresses
   * belong to real chamber operators.
   */
  const forgot = useMutation({
    mutationFn: () => api.anon('/api/console/auth/forgot-password', { email: forgotEmail.trim() }),
    onSuccess: () => setForgotOpen(false),
    onError: () => setForgotOpen(false),
  });

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values.email, values.password);
    } catch (error) {
      // The server's message is the accurate one — it knows whether the account is
      // locked, inactive, or simply wrong — so it is shown rather than replaced.
      setFormError(
        error instanceof ApiError ? error.message : 'Could not sign in. Try again in a moment.',
      );
    }
  });

  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      {/* The editorial register — this is a marketing-adjacent surface, not a data grid. */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[var(--pv-ink)] p-12 text-[var(--pv-on-ink)] lg:flex">
        <div className="flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-leaf)] [font-size:15px] font-bold text-[var(--pv-leaf-ink)]">
            P
          </span>
          <span className="[font-size:17px] font-bold tracking-[-0.02em]">Provenir</span>
        </div>

        <div className="max-w-[34ch]">
          <p className="pv-eyebrow text-[var(--pv-leaf)]">Certificates of origin</p>
          <h1 className="pv-display mt-3 text-[var(--pv-on-ink)]">
            Proof of where it came from.
          </h1>
          <p className="mt-5 [font-size:14.5px] leading-relaxed text-[rgba(244,245,236,0.7)]">
            The platform Australian Chambers of Commerce use to receive, review and issue
            certificates of origin — and that customs officers, banks and importers use to
            confirm one is genuine.
          </p>
        </div>

        <div className="flex items-center gap-2 [font-size:12.5px] text-[rgba(244,245,236,0.55)]">
          <ShieldCheck aria-hidden className="size-4" />
          Every issuance is recorded, attributed and auditable.
        </div>

        {/* A seal motif, low-contrast, purely decorative — hence aria-hidden. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -right-32 size-[26rem] rounded-full border-[3px] border-[rgba(195,219,156,0.14)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -right-24 size-[22rem] rounded-full border border-[rgba(195,219,156,0.1)]"
        />
      </div>

      <div className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-[26rem]">
          <div className="lg:hidden">
            <span className="grid size-10 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-ink)] [font-size:15px] font-bold text-[var(--pv-leaf)]">
              P
            </span>
          </div>

          <h2 className="mt-6 [font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em] lg:mt-0">
            Sign in
          </h2>
          <p className="mt-2 [font-size:14px] leading-relaxed text-[var(--pv-muted)]">
            Use the email address your chamber administrator invited.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4">
            {endedBecause && !formError && (
              <Callout tone="info" icon={<Info className="size-4" />}>
                {endedBecause === 'idle'
                  ? 'You were signed out after 30 minutes without activity. Sign in to carry on.'
                  : endedBecause === 'elsewhere'
                    ? 'You signed out in another tab.'
                    : 'Your session ended. Sessions last at most 12 hours. Sign in to carry on.'}
              </Callout>
            )}
            {formError && <Callout tone="stop" icon={<Info className="size-4" />}>{formError}</Callout>}

            <Field label="Email address" required error={form.formState.errors.email?.message}>
              <Input
                type="email"
                autoComplete="username"
                autoFocus
                placeholder="you@chamber.org.au"
                {...form.register('email')}
              />
            </Field>

            <Field label="Password" required error={form.formState.errors.password?.message}>
              <Input type="password" autoComplete="current-password" {...form.register('password')} />
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={form.formState.isSubmitting}
              className="mt-1 w-full"
            >
              Sign in
            </Button>
          </form>

          {/* The reset path exists now (T-116). It used to promise one that did not, read
              by exactly the people least able to work around it. */}
          {forgot.isSuccess ? (
            <p className="mt-6 [font-size:12.5px] leading-relaxed text-[var(--pv-leaf-ink)]">
              If that address belongs to an account, a link to set a new password is on its way.
              It works once and lasts two hours.
            </p>
          ) : forgotOpen ? (
            <form
              className="mt-6 flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                forgot.mutate();
              }}
            >
              <Field label="Your email address" help="We will send a link to set a new password.">
                <Input
                  type="email"
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
              </Field>
              <div className="flex gap-2">
                <Button type="submit" variant="secondary" size="sm" loading={forgot.isPending}>
                  Send me a link
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setForgotOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="mt-6 [font-size:12.5px] leading-relaxed text-[var(--pv-faint)]">
              Never signed in before? Use the link in your invitation email.{' '}
              <button
                type="button"
                className="underline hover:text-[var(--pv-ink)]"
                onClick={() => {
                  setForgotEmail(form.getValues('email') ?? '');
                  setForgotOpen(true);
                }}
              >
                Forgotten your password?
              </button>
            </p>
          )}

          {import.meta.env.DEV && (
            <div className="mt-8 rounded-[var(--pv-radius)] border border-dashed border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] p-3.5">
              <p className="pv-eyebrow text-[var(--pv-faint)]">Seeded accounts · development only</p>
              <ul className="mt-2 flex list-none flex-col gap-1 p-0 [font-size:12px] text-[var(--pv-ink-soft)]">
                {[
                  ['operator@bcq.local', 'BCQ operator'],
                  ['admin@bcq.local', 'BCQ chamber admin'],
                  ['operator@nswbc.local', 'Business NSW operator'],
                  ['platform@provenance.local', 'Platform super admin'],
                ].map(([email, role]) => (
                  <li key={email} className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        form.setValue('email', email!);
                        form.setValue('password', 'provenance-demo-2026');
                      }}
                      className="pv-mono [font-size:11.5px] text-[var(--pv-leaf-deep)] underline decoration-dotted"
                    >
                      {email}
                    </button>
                    <span className="text-[var(--pv-faint)]">{role}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 [font-size:11px] text-[var(--pv-faint)]">
                Password <span className="pv-mono">provenance-demo-2026</span>. Click an address to fill the form.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
